import { useCallback, useEffect, useState } from 'react'

import { useAutomatedMessageContext } from '@liveHelper/components/AutomatedMessageProvider'
import { MessagePreviewCloseButton } from '@liveHelper/components/MessagePreview/MessagePreview.styles'
import { useMessagePreviewContext } from '@liveHelper/components/MessagePreviewProvider'
import { useChatChannelConfig } from '@liveHelper/context/ChatChannelConfigContext'
import {
  actionsSelector,
  isIdleStateTextEligibleToShowSelector,
  useStore,
  useStoreApi,
} from '@liveHelper/models/store'
import { useTrackClickWidget } from '@liveHelper/tracking/useLiveHelperTracking'

import { Close } from '@core/assets/Close'
import { useContextTranslation } from '@core/i18n'

import { IdleStateTextBox } from './IdlePopupNotification.styles'

const IDLE_NOTIFICATION_DELAY = 30000
const IDLE_NOTIFICATION_DISMISS_TIME = 30000

/**
 * Idle state text notification component.
 */
export const IdlePopupNotification = () => {
  const storeApi = useStoreApi()
  const chatChannelConfig = useChatChannelConfig()
  const { t } = useContextTranslation()
  const { lastUnreadMessage } = useMessagePreviewContext()
  const { automatedMessage } = useAutomatedMessageContext()
  const { sendUIStateTransitionEvent } = useStore(actionsSelector)
  const [isVisible, setIsVisible] = useState(false)

  const trackClickWidget = useTrackClickWidget()

  const content = chatChannelConfig?.widget_style?.idle_state_text
  const isEligible = useStore(isIdleStateTextEligibleToShowSelector)

  useEffect(() => {
    if (content && isEligible) {
      const timeout = setTimeout(() => {
        setIsVisible(true)
      }, IDLE_NOTIFICATION_DELAY)
      return () => clearTimeout(timeout)
    }
  }, [content, isEligible])

  useEffect(() => {
    if (isVisible) {
      const timeout = setTimeout(() => {
        setIsVisible(false)
        const { setIdleStateTextClosedAutomatically } = actionsSelector(
          storeApi.getState(),
        )
        setIdleStateTextClosedAutomatically(true)
      }, IDLE_NOTIFICATION_DISMISS_TIME)
      return () => clearTimeout(timeout)
    }
  }, [isVisible, storeApi])

  const handleClose = useCallback(
    (event) => {
      event.stopPropagation()
      setIsVisible(false)
      const { dismissIdleStateText } = actionsSelector(storeApi.getState())
      dismissIdleStateText()
    },
    [storeApi],
  )

  const handleClick = useCallback(() => {
    trackClickWidget({
      click_target: 'minimized_widget',
    })
    sendUIStateTransitionEvent('EXPAND')
  }, [trackClickWidget, sendUIStateTransitionEvent])

  if (lastUnreadMessage || automatedMessage || !content) {
    return null
  }

  return (
    <IdleStateTextBox
      isVisible={isVisible}
      className="multiAgentMessageBox"
      onClick={handleClick}
    >
      <MessagePreviewCloseButton
        aria-label={t(`oneToOne:Dismiss notification`)}
        actionType="neutral"
        buttonSize="xs"
        shape="circle"
        onClick={handleClose}
        icon={<Close height="8px" width="8px" label="" aria-hidden />}
      />
      {content}
    </IdleStateTextBox>
  )
}
